import { useReducedMotion } from "motion/react";
import { useContext, useEffect, useId, useRef, useState } from "react";
import { useQueryClient } from "react-query";

import {
  AgentsIcon,
  Box,
  ChakraVisuallyHidden,
  Column,
  ExternalLinkIcon,
  ImageIcon,
  MailIcon,
  Row,
  Text,
  TextWithTooltip,
  Tooltip,
} from "@hightouch/htui";
import { InteractionStatus } from "@hightouch/lib/llm/schema";
import { AgentType } from "@hightouch/lib/llm/shared";

import { AccordionCard } from "src/components/accordion-card";
import { RowButton } from "src/components/folders/row-button";
import type { ResourceAgentComposerAccessoryProps } from "src/components/layout/page-sidebar/agent/resource-agent-sidebar/types";
import {
  type AmpSubagentSessionsQuery,
  useAmpSubagentSessionsQuery,
} from "src/graphql";
import { useGovernedRefetchInterval } from "src/hooks/use-governed-refetch-interval";
import { useIsHightouchStaff } from "src/hooks/use-is-hightouch-staff";
import { Link } from "src/router";

import { refreshAmpFiles } from "src/pages/projects/amp-session";
import {
  CanvasThreadTargetsContext,
  useReportRunningThreads,
} from "src/pages/projects/library/canvas/canvas-thread-targets";

type SubagentSession = AmpSubagentSessionsQuery["llm_chat_sessions"][number];

const SUBAGENT_ICONS = new Map<string, typeof AgentsIcon>([
  [AgentType.AdStudioAdGenerator, ImageIcon],
  [AgentType.AdStudioTemplateGenerator, ImageIcon],
  [AgentType.Content, MailIcon],
  [AgentType.ContentNodeEditing, MailIcon],
  [AgentType.LCM, MailIcon],
]);

function subagentIcon(agentType: unknown) {
  return (
    (typeof agentType === "string"
      ? SUBAGENT_ICONS.get(agentType)
      : undefined) ?? AgentsIcon
  );
}

function subagentInteraction(session: SubagentSession) {
  return session.activeInteraction[0] ?? session.latestInteraction[0];
}

function isRunning(session: SubagentSession) {
  return subagentInteraction(session)?.status === InteractionStatus.Pending;
}

/** The manifest keeps discovering new children even after the parent finishes its turn. */
export function AmpSubagents({
  sessionId,
  isOpen,
  isParentBusy,
}: ResourceAgentComposerAccessoryProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const runningStatusId = useId();
  const { refetchInterval: activeInterval } =
    useGovernedRefetchInterval<AmpSubagentSessionsQuery>({
      baseMs: 3000,
      maxMs: 6000,
    });
  const { refetchInterval: idleInterval } =
    useGovernedRefetchInterval<AmpSubagentSessionsQuery>({
      baseMs: 5000,
      maxMs: 15000,
    });
  const { data, isError, refetch } = useAmpSubagentSessionsQuery(
    { parentSessionId: sessionId },
    {
      enabled: isOpen,
      refetchOnMount: "always",
      refetchOnWindowFocus: "always",
      refetchInterval: (data, query) => {
        if (!isOpen) return false;
        const working = isParentBusy || data?.llm_chat_sessions.some(isRunning);
        return working
          ? activeInterval(data, query)
          : idleInterval(data, query);
      },
    },
  );
  const sessions = (data?.llm_chat_sessions ?? []).filter(isRunning);
  const queryClient = useQueryClient();
  // A subagent often finishes after the AMP chat's turn, so its end refreshes the files it may have saved.
  const runningIds = useRef(new Set<string>());
  useEffect(() => {
    if (!data) return;
    const running = new Set(
      data.llm_chat_sessions.filter(isRunning).map((session) => session.id),
    );
    if ([...runningIds.current].some((id) => !running.has(id)))
      refreshAmpFiles(queryClient);
    runningIds.current = running;
  }, [data, queryClient]);
  const runningThreadCount = sessions.length;
  // Only while the chat is open and polling them: a closed chat's last read would go stale.
  useReportRunningThreads(isOpen ? sessions.map(({ id }) => id) : []);
  if (sessions.length === 0) return null;

  return (
    <>
      <ChakraVisuallyHidden id={runningStatusId} role="status">
        {runningThreadCount > 0 ? `${runningThreadCount} working` : ""}
      </ChakraVisuallyHidden>
      <AccordionCard
        variant="thin"
        mb={2}
        borderColor="base.divider"
        borderRadius="lg"
        boxShadow="none"
        bg="transparent"
        textAlign="left"
        isOpen={isExpanded}
        onToggle={() => {
          const expanded = !isExpanded;
          setIsExpanded(expanded);
          if (isOpen && expanded) void refetch();
        }}
        buttonProps={{
          "aria-label": isExpanded ? "Hide threads" : "Show threads",
          "aria-describedby":
            runningThreadCount > 0 ? runningStatusId : undefined,
        }}
        header={
          <Row gap={2} align="center" flex={1} minWidth={0}>
            <Box
              as={AgentsIcon}
              boxSize={4}
              color="text.secondary"
              flexShrink={0}
            />
            <Text size="sm" fontWeight="medium" color="text.primary">
              Threads · {sessions.length}
            </Text>
            <Box flex={1} />
            {runningThreadCount > 0 && (
              <Text size="sm" color="text.secondary">
                {runningThreadCount} working
              </Text>
            )}
            {isError && (
              <Text size="sm" color="text.secondary">
                Status unavailable
              </Text>
            )}
          </Row>
        }
      >
        {isExpanded && (
          <Column
            as="ul"
            aria-label="Threads"
            m={0}
            p={0}
            listStyleType="none"
            maxHeight={60}
            overflowY="auto"
          >
            {sessions.map((session) => (
              <SubagentRow key={session.id} session={session} />
            ))}
          </Column>
        )}
      </AccordionCard>
    </>
  );
}

function SubagentRow({ session }: { session: SubagentSession }) {
  const runningStatusId = useId();
  const isHightouchStaff = useIsHightouchStaff();
  const reduceMotion = useReducedMotion();
  const { targets, onFocus } = useContext(CanvasThreadTargetsContext);
  const nodeIds = targets.get(session.id);
  const canFocus = Boolean(nodeIds?.length);
  const icon = subagentIcon(session.agentType);
  const title = session.name || "Untitled thread";
  const interaction = subagentInteraction(session);
  const isPending = interaction?.status === InteractionStatus.Pending;
  const titleContent = (
    <>
      <Box as={icon} boxSize={4} color="text.secondary" flexShrink={0} />
      <Box flex={1} minWidth={0} position="relative" zIndex={1}>
        <Box width="fit-content" maxWidth="100%">
          <TextWithTooltip
            size="sm"
            fontWeight="medium"
            color="text.primary"
            shimmer={isPending && !reduceMotion}
            shimmerColor="gray.400"
          >
            {title}
          </TextWithTooltip>
        </Box>
      </Box>
    </>
  );

  return (
    <Column
      as="li"
      aria-busy={isPending}
      aria-describedby={isPending ? runningStatusId : undefined}
      position="relative"
      isolation="isolate"
      cursor={canFocus ? "pointer" : undefined}
      _hover={canFocus ? { bg: "base.backgroundHover" } : undefined}
      gap={1}
      px={3}
      py={2}
      borderTopWidth="hairline"
      borderColor="base.divider"
    >
      <Row gap={2} align="center" minWidth={0}>
        {canFocus ? (
          <RowButton
            overlayRadius="none"
            aria-label={`Show ${title} on canvas`}
            aria-describedby={isPending ? runningStatusId : undefined}
            onClick={() => onFocus(nodeIds ?? [])}
            gap={2}
            align="center"
            flex={1}
            minWidth={0}
            cursor="pointer"
          >
            {titleContent}
          </RowButton>
        ) : (
          <Row gap={2} align="center" flex={1} minWidth={0}>
            {titleContent}
          </Row>
        )}
        {isPending && (
          <Box
            as={reduceMotion ? "span" : ChakraVisuallyHidden}
            id={runningStatusId}
            flexShrink={0}
          >
            <Text size="sm" color="text.secondary">
              Running
            </Text>
          </Box>
        )}
        {isHightouchStaff && (
          <Tooltip
            message={`Open ${title} chat in a new tab (Hightouch staff only)`}
          >
            <Link
              href={`/agents/chats/${session.id}`}
              target="_blank"
              rel="noopener noreferrer"
              position="relative"
              zIndex={1}
              flexShrink={0}
              aria-label={`Open ${title} chat in a new tab (Hightouch staff only)`}
            >
              <Box as={ExternalLinkIcon} boxSize={3} color="text.tertiary" />
            </Link>
          </Tooltip>
        )}
      </Row>
      {interaction?.status === InteractionStatus.Failed && (
        <Text size="sm" color="text.danger">
          Failed
        </Text>
      )}
      {interaction?.status === InteractionStatus.Canceled && (
        <Text size="sm" color="text.tertiary">
          Stopped
        </Text>
      )}
    </Column>
  );
}
